import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowRight, Globe, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { auditStatsQueryOptions } from '@/lib/audit';

/**
 * Rolling 24h security snapshot for audit.read holders. Two cheap
 * COUNT queries on the server (total events + distinct IPs) — no row
 * payload — so this stays fast even when audit grows large.
 *
 * Pairs with FailedLoginsCard (which is silent-by-default for the
 * focused signal) — this one always renders so admins get a baseline
 * "system pulse" they can compare against day-over-day.
 */
export function SecurityStatsCard() {
  const q = useQuery(auditStatsQueryOptions());
  const stats = q.data;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldAlert className="h-4 w-4 text-slate-400" />
          กิจกรรม 24 ชม.
        </CardTitle>
        <Link
          to="/audit"
          className="flex items-center text-xs text-blue-600 hover:underline dark:text-blue-400"
        >
          ดู audit <ArrowRight className="ml-0.5 h-3 w-3" />
        </Link>
      </CardHeader>
      <CardContent>
        {q.isLoading ? (
          <p className="text-sm text-slate-500">กำลังโหลด...</p>
        ) : !stats ? (
          <p className="text-sm text-slate-400">ดึงข้อมูลไม่สำเร็จ</p>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-slate-50">
                {stats.totalEvents.toLocaleString('th-TH')}
              </div>
              <div className="mt-0.5 text-[11px] text-slate-500">เหตุการณ์ทั้งหมด</div>
            </div>
            <div>
              <div className="flex items-baseline gap-1.5 text-2xl font-bold text-slate-900 dark:text-slate-50">
                <Globe className="h-4 w-4 self-center text-slate-400" />
                {stats.distinctIpCount.toLocaleString('th-TH')}
              </div>
              <div className="mt-0.5 text-[11px] text-slate-500">IP ที่ไม่ซ้ำ</div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
